/**
 * Exact money text for the costing workbench (batch 8 T11, docs/NUMERIC-PRECISION.md).
 *
 * A unit cost is stored by the API as cents with four decimals (`"100.1250"`). Users type euros, so a
 * euro text with up to six decimals is converted by moving the decimal point on the digits, never through
 * a float. Totals are exact `bigint` sums of quantity x cost in units of 0.0001 cent and are rounded to a
 * whole cent once, half up, at the end. Nothing here guesses a cost: empty or malformed text is an error,
 * `0` is a real zero.
 */

/** 0.0001 cent = 0.000001 euro. */
const CENTS4_PER_EURO = 1_000_000n;
const CENTS4_PER_CENT = 10_000n;

/** Same bound the API enforces on one unit cost: 1,000,000 EUR, in 0.0001 cent. */
export const MAX_UNIT_COST_CENTS4 = 100_000_000n * CENTS4_PER_CENT;

export type EuroParseFailure = 'empty' | 'format' | 'negative' | 'precision' | 'range';

export type EuroParse =
  | { readonly ok: true; readonly cents4: bigint; readonly text: string }
  | { readonly ok: false; readonly reason: EuroParseFailure };

/** `"100.1250"` for 1001250 (the API's cents text). */
export function formatCents4Text(cents4: bigint): string {
  const negative = cents4 < 0n;
  const abs = negative ? -cents4 : cents4;
  const whole = abs / CENTS4_PER_CENT;
  const fraction = (abs % CENTS4_PER_CENT).toString().padStart(4, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${fraction}`;
}

/** The API's cents text (`"100.1250"`, `"0"`) as 0.0001-cent units; `null` when it is not an exact decimal. */
export function parseCents4Text(text: string): bigint | null {
  const match = /^(\d+)(?:\.(\d{1,4}))?$/.exec(text.trim());
  if (!match) return null;
  return BigInt(match[1] ?? '0') * CENTS4_PER_CENT + BigInt((match[2] ?? '').padEnd(4, '0'));
}

/**
 * User text in euros (`12,5`, `12.50`, `0,00125`) to the API's cents text. Up to six decimals (the
 * four-decimal cent), comma or dot, no sign, no thousands separators, no exponent.
 */
export function parseEuroInput(input: string): EuroParse {
  const raw = input.trim().replace(',', '.');
  if (raw === '') return { ok: false, reason: 'empty' };
  if (raw.startsWith('-')) return { ok: false, reason: 'negative' };
  const match = /^(\d{1,12})(?:\.(\d+))?$/.exec(raw);
  if (!match) return { ok: false, reason: 'format' };
  const fraction = match[2] ?? '';
  if (fraction.length > 6) return { ok: false, reason: 'precision' };
  const cents4 = BigInt(match[1] ?? '0') * CENTS4_PER_EURO + BigInt(fraction.padEnd(6, '0'));
  if (cents4 > MAX_UNIT_COST_CENTS4) return { ok: false, reason: 'range' };
  return { ok: true, cents4, text: formatCents4Text(cents4) };
}

/** Exact `quantity x unit cost`. */
export function valueCents4(unitCents4: bigint, quantity: number): bigint {
  return unitCents4 * BigInt(quantity);
}

/** A summed exact total to whole cents, once, half up (totals are never negative here). */
export function roundCents4ToCents(total: bigint): bigint {
  const twice = total * 2n + CENTS4_PER_CENT;
  const divisor = CENTS4_PER_CENT * 2n;
  const quotient = twice / divisor;
  return twice % divisor !== 0n && twice < 0n ? quotient - 1n : quotient;
}

function groupInteger(value: bigint, language: string): string {
  return new Intl.NumberFormat(language, { useGrouping: true }).format(value);
}

function decimalSeparator(language: string): string {
  return language.startsWith('en') ? '.' : ',';
}

function withCurrency(amount: string, language: string): string {
  // Finnish puts a non-breaking space before the sign so an amount never wraps from it.
  return language.startsWith('en') ? `€${amount}` : `${amount}\u00a0€`;
}

/** Euro text of a count of 0.0001 cents: at least two decimals, up to six, never rounded. */
export function formatEuroFromCents4(cents4: bigint, language: string): string {
  const negative = cents4 < 0n;
  const abs = negative ? -cents4 : cents4;
  const euros = abs / CENTS4_PER_EURO;
  let fraction = (abs % CENTS4_PER_EURO).toString().padStart(6, '0');
  while (fraction.length > 2 && fraction.endsWith('0')) fraction = fraction.slice(0, -1);
  const amount = `${negative ? '-' : ''}${groupInteger(euros, language)}${decimalSeparator(language)}${fraction}`;
  return withCurrency(amount, language);
}

/** Euro text of whole cents (a total rounded once): always two decimals. */
export function formatEuroFromCents(cents: number | bigint, language: string): string {
  const value = BigInt(cents);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const euros = abs / 100n;
  const fraction = (abs % 100n).toString().padStart(2, '0');
  return withCurrency(`${negative ? '-' : ''}${groupInteger(euros, language)}${decimalSeparator(language)}${fraction}`, language);
}

/** Euro text of the API's four-decimal cents text; `null` for text that is not exact. */
export function formatEuroFromCentsText(text: string, language: string): string | null {
  const cents4 = parseCents4Text(text);
  return cents4 === null ? null : formatEuroFromCents4(cents4, language);
}

/**
 * Plain euro text for an input box (`1.00125`, `12.50`, dot separator, at least two decimals), the exact
 * inverse of {@link parseEuroInput}. Used when the user deliberately adopts a hint.
 */
export function euroInputFromCents4(cents4: bigint): string {
  const abs = cents4 < 0n ? -cents4 : cents4;
  let fraction = (abs % CENTS4_PER_EURO).toString().padStart(6, '0');
  while (fraction.length > 2 && fraction.endsWith('0')) fraction = fraction.slice(0, -1);
  return `${(abs / CENTS4_PER_EURO).toString()}.${fraction}`;
}

/** Whole cents (a catalog or supplier price) as 0.0001-cent units. */
export function cents4FromWholeCents(cents: number): bigint {
  return BigInt(cents) * CENTS4_PER_CENT;
}
